// Quiz Scheduler Component - Handles quiz timing and availability
import React from "react";
import { QuizSchedule } from "../../../types/quiz";

interface QuizSchedulerProps {
  schedule?: QuizSchedule;
  onChange: (schedule: QuizSchedule) => void;
  disabled?: boolean;
}

const QuizScheduler: React.FC<QuizSchedulerProps> = ({
  schedule,
  onChange,
  disabled = false,
}) => {
  const handleChange = (field: keyof QuizSchedule, value: string) => {
    onChange({
      ...schedule,
      [field]: value || undefined,
    });
  };

  // Get current date-time for min attribute in local time
  const now = new Date();
  const currentDateTime = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(
    now.getHours()
  ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  // Helper function to convert ISO string to local datetime-local format
  // Also handles if the value is already in local datetime format
  const toLocalDateTime = (value: string | undefined) => {
    if (!value) return "";

    // If it's already in YYYY-MM-DDTHH:mm format (local datetime), return as-is
    if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      return value;
    }

    // Otherwise, treat as ISO string and convert to local
    const date = new Date(value);
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to convert local datetime-local value to ISO string
  const toISOString = (localDateTime: string) => {
    if (!localDateTime) return "";
    // Create date object from local datetime string and convert to ISO
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Quiz Scheduling
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Configure when students can access this quiz. All fields are
                optional - leave blank for immediate availability.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Available From
            <span className="text-xs text-gray-500 ml-1">(Optional)</span>
          </label>
          <input
            type="datetime-local"
            value={toLocalDateTime(schedule?.availableFrom)}
            onChange={(e) =>
              handleChange(
                "availableFrom",
                e.target.value ? toISOString(e.target.value) : ""
              )
            }
            min={currentDateTime}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            When students can start taking the quiz
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Available Until
            <span className="text-xs text-gray-500 ml-1">(Optional)</span>
          </label>
          <input
            type="datetime-local"
            value={toLocalDateTime(schedule?.availableUntil)}
            onChange={(e) =>
              handleChange(
                "availableUntil",
                e.target.value ? toISOString(e.target.value) : ""
              )
            }
            min={toLocalDateTime(schedule?.availableFrom) || currentDateTime}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            When quiz access ends (hard deadline)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Due Date
            <span className="text-xs text-gray-500 ml-1">(Optional)</span>
          </label>
          <input
            type="datetime-local"
            value={toLocalDateTime(schedule?.dueDate)}
            onChange={(e) =>
              handleChange(
                "dueDate",
                e.target.value ? toISOString(e.target.value) : ""
              )
            }
            min={toLocalDateTime(schedule?.availableFrom) || currentDateTime}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            Recommended completion date
          </p>
        </div>
      </div>

      {/* Schedule Preview */}
      {(schedule?.availableFrom ||
        schedule?.availableUntil ||
        schedule?.dueDate) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Schedule Preview
          </h4>
          <div className="space-y-2 text-sm">
            {schedule?.availableFrom && (
              <div className="flex justify-between">
                <span className="text-gray-600">Opens:</span>
                <span className="font-medium">
                  {new Date(schedule.availableFrom).toLocaleString()}
                </span>
              </div>
            )}

            {schedule?.dueDate && (
              <div className="flex justify-between">
                <span className="text-gray-600">Due:</span>
                <span className="font-medium">
                  {new Date(schedule.dueDate).toLocaleString()}
                </span>
              </div>
            )}

            {schedule?.availableUntil && (
              <div className="flex justify-between">
                <span className="text-gray-600">Closes:</span>
                <span className="font-medium text-red-600">
                  {new Date(schedule.availableUntil).toLocaleString()}
                </span>
              </div>
            )}

            {!schedule?.availableFrom &&
              !schedule?.availableUntil &&
              !schedule?.dueDate && (
                <div className="text-center text-gray-500 italic">
                  Quiz will be available immediately and remain open
                  indefinitely
                </div>
              )}
          </div>
        </div>
      )}

      {/* Validation Warnings */}
      {schedule?.availableFrom &&
        schedule?.availableUntil &&
        new Date(schedule.availableFrom) >=
          new Date(schedule.availableUntil) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Invalid Schedule
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    "Available Until" date must be after "Available From" date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {schedule?.availableFrom &&
        schedule?.dueDate &&
        new Date(schedule.availableFrom) >= new Date(schedule.dueDate) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Schedule Warning
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Due date should typically be after the available from date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Quick Schedule Templates */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">
          Quick Templates
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              const oneWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
              onChange({
                availableFrom: now.toISOString(),
                dueDate: oneWeek.toISOString(),
              });
            }}
            disabled={disabled}
            className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Available Now - Due in 1 Week
          </button>

          <button
            type="button"
            onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(9, 0, 0, 0);

              const oneWeek = new Date(
                tomorrow.getTime() + 7 * 24 * 60 * 60 * 1000
              );
              onChange({
                availableFrom: tomorrow.toISOString(),
                dueDate: oneWeek.toISOString(),
              });
            }}
            disabled={disabled}
            className="px-3 py-2 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Tomorrow 9 AM
          </button>

          <button
            type="button"
            onClick={() => onChange({})}
            disabled={disabled}
            className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizScheduler;
